import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';

export type ConfirmationDialogProps = {
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isConfirming?: boolean;
  destructive?: boolean;
  hideCancel?: boolean;
  disableBackdropClose?: boolean;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  icon?: React.ReactNode;
  autoFocusConfirm?: boolean;
  children?: React.ReactNode;
};

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title = '確認',
  message,
  confirmText = 'OK',
  cancelText = 'キャンセル',
  onConfirm,
  onCancel,
  isConfirming = false,
  destructive = false,
  hideCancel = false,
  disableBackdropClose = false,
  fullWidth = true,
  maxWidth = 'xs',
  icon,
  autoFocusConfirm = false,
  children,
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open && autoFocusConfirm && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [open, autoFocusConfirm]);

  const handleClose = (_: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (!isConfirming) onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={fullWidth} maxWidth={maxWidth}>
      {title && (
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {icon}
            <span>{title}</span>
          </Stack>
        </DialogTitle>
      )}

      <DialogContent dividers>
        {children
          ? children
          : message && (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {message}
              </Typography>
            )}
      </DialogContent>

      <DialogActions>
        {!hideCancel && (
          <Button onClick={onCancel} disabled={isConfirming} color="inherit" variant="text">
            {cancelText}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          disabled={isConfirming}
          color={destructive ? 'error' : 'primary'}
          variant={destructive ? 'contained' : 'contained'}
          autoFocus={autoFocusConfirm}
          ref={confirmButtonRef}
          startIcon={isConfirming ? <CircularProgress size={16} /> : undefined}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
